import {
  Box,
  Button,
  ColumnDatas,
  Icons,
  Table,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import { useEffect, useRef, useState } from "react";
import "./index.scss";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import utilsCommon from "../../shared/utils/common";
import AdminFilter from "../../components/AdminFilter";
import { FilterData } from "../../types/adminFilter";
import useAdminLogStore from "../../redux/dispatcher/useAdminLogStore";
import { LogInfo } from "../../types/adminLog";
import EmptyFilterPage from "../../components/EmptyPage/EmptyFilterPage";
import { Workgroup } from "../../types/workgroup";
import useAdminWorkgroupStore from "../../redux/dispatcher/useAdminWorkgroupStore";
import utilsDocs from "../../shared/utils/docs";
import DocAside from "../../components/DocAside";
import useLogStore from "../../redux/dispatcher/useLogStore";
import { SIDE_WORKGROUPLIST_FILTER } from "../../BaseLayout";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import LogSettingButton from "../../pages/Workgroup/WorkgroupLog/LogSettingButton";
import { LogItem } from "../../types/files";
import { handleDocPreview } from "../../shared/utils/api";
import classNames from "classnames";
import EmptyInitFilterPage from "../../components/EmptyPage/EmptyInitFilterPage";
import FilesharePolicyTooltip from "../../pages/Workgroup/WorkgroupLog/FilesharePolicyTooltip";
import WorkgroupPolicyTooltip from "../../pages/Workgroup/WorkgroupLog/WorkgroupPolicyTooltip";

interface LogProps {
  isAdmin: boolean;
}
const DocLog: React.FC<LogProps> = ({ isAdmin }) => {
  const dir = document.documentElement.dir; // 'ltr' 또는 'rtl'

  const { t } = useTranslation();
  const toast = useToast();
  const { adminLogStore, getDocLog, getDocLogExcel } = useAdminLogStore();
  const { adminWorkgroupStore, getWorkgroupList } = useAdminWorkgroupStore();
  const { workgroupState, getWorkgroupSideList } = useWorkgroupStore();
  const { logState, getDocUserLog, getDocUserLogExcel } = useLogStore();

  /**** State 모음 ****/
  const initFilterData = {
    fileName: "",
    userId: "",
    workgroupId: "",
    workgroupIds: [],
    dateFilter: "1month",
    date: {
      start: utilsCommon.getStartPeriod("1month"),
      end: dayjs().endOf("day").valueOf(),
    },
    order: "datedesc",
    saveTypes: [],
  } as FilterData;
  const pageCount = 50;

  const [initNum, changeInitNum] = useState(0);
  const [filterData, setFilterData] = useState<FilterData>(initFilterData);
  const [docLogs, setDocLogs] = useState([] as LogInfo[]);
  const [nextPage, setNextPage] = useState(-1);
  const [workgroupList, setWorkgroupList] = useState([] as Workgroup[]);
  const [hubWorkgroupList, changeHubWorkgroupList] = useState([] as string[]);
  const [height, setHeight] = useState(window.innerHeight - 260);
  const tableRef = useRef<HTMLDivElement>(null);
  const [initDone, setInitDone] = useState(false);
  const [asideId, changeAsideId] = useState("");
  const [showDocAside, setShowDocAside] = useState<boolean>(false);
  const [settingButtonInfo, setSettingInfo] = useState<{
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
  }>({
    x: 0,
    y: 0,
    logId: "",
    log: undefined,
  }); // 워크그룹 더보기 메뉴 setting
  /**** UseEffect 모음 ****/
  useEffect(() => {
    getDocLogList();
    const WORKGROUP_LIST_FILTER = {
      orderBy: "workgroupName",
      orderByType: "ASC",
      status: -1,
      posPage: 1,
      pageRange: 1,
      rowRange: 0,
      searchDate: {
        start: 0,
        end: 0,
      },
      workgroupName: "",
      managerId: "",
      isDetail: "",
    };
    if (isAdmin) {
      getWorkgroupList(WORKGROUP_LIST_FILTER);
    } else {
      getWorkgroupSideList(SIDE_WORKGROUPLIST_FILTER);
    }

    setInitDone(true);
  }, []);

  useEffect(() => {
    if (adminLogStore.docLog.posPage === 1) {
      setDocLogs(adminLogStore.docLog.logResults);
    } else {
      setDocLogs(docLogs.concat(adminLogStore.docLog.logResults));
    }
    if (adminLogStore.docLog.posPage * pageCount < adminLogStore.docLog.total) {
      setNextPage(adminLogStore.docLog.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [adminLogStore.docLog]);
  useEffect(() => {
    if (logState?.contentLogList?.posPage === 1) {
      setDocLogs(logState?.contentLogList?.logResults);
    } else {
      setDocLogs(docLogs.concat(logState?.contentLogList?.logResults));
    }
    if (
      logState?.contentLogList?.posPage * pageCount <
      logState?.contentLogList?.total
    ) {
      setNextPage(logState?.contentLogList?.posPage + 1);
    } else {
      setNextPage(-1);
    }
  }, [logState?.contentLogList]);

  useEffect(() => {
    if (adminWorkgroupStore.usingWorkgroup.workgroupList?.length > 0) {
      setWorkgroupList(adminWorkgroupStore.usingWorkgroup.workgroupList);
    }
  }, [adminWorkgroupStore.usingWorkgroup.workgroupList]);

  useEffect(() => {
    if (workgroupState.workgroupSidemenuList?.length > 0) {
      setWorkgroupList(workgroupState.workgroupSidemenuList);
      changeHubWorkgroupList(
        workgroupState.workgroupSidemenuList
          ?.filter((item: Workgroup) => item.workgroupType === "HUB")
          ?.map((item: Workgroup) => item.workgroupId)
      );
    }
  }, [workgroupState.workgroupSidemenuList]);
  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const rect = tableRef.current.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    };

    const element = tableRef.current;

    const resizeObserver = new ResizeObserver(() => {
      if (element) {
        const rect = element.getBoundingClientRect();
        setHeight(window.innerHeight - rect.top - 72);
      }
    });

    if (element) {
      resizeObserver.observe(element);
    }
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (element) {
        resizeObserver.unobserve(element);
      }
    };
  }, []);

  useEffect(() => {
    initDone && getDocLogList();
  }, [filterData]);

  const getDocLogList = (page?: number) => {
    if (page !== -1) {
      const filter = {
        posPage: page ?? 1,
        pageRange: 1,
        rowRange: pageCount,
        filterDocument:
          filterData.saveTypes && filterData.saveTypes.length > 0
            ? filterData.saveTypes
            : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
        filterWorkgroup: [],
        filterWebMeeting: [],
        filterChat: [],
        startDate:
          filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
        endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
        order: filterData.order + "",
        userId: filterData.userId + "",
        documentName: filterData.fileName + "",
        workgroupId: filterData.workgroupId ?? "",
        workgroupIdList: filterData.workgroupIds ?? [],
      };
      if (isAdmin) getDocLog(filter);
      else getDocUserLog(filter);
      if (page === undefined) {
        document.getElementById("docTable")?.scrollTo(0, 0);
      }
    }
  };

  const getAgentTypeStr = (agentType: number) => {
    const AgentStr = [
      "N/A",
      "Windows",
      "Mac",
      "Web",
      "Mobile",
      "Outlook",
      "FSM",
      "N/A",
    ];

    return AgentStr[agentType];
  };

  function transformFileList(data: any[] | undefined) {
    const dataTransformed = [] as ColumnDatas[];
    data &&
      data?.map((item: any, index: number) => {
        if (item && item?.workgroupId && item?.workgroupName) {
          const dataItemTransformed = {
            data: [
              {
                columnText: "",
              },
              {
                columnText: utilsCommon.renderDateFormat(item.logDate),
              },
              {
                columnText: (
                  <Box alignItems="center">
                    <span style={{ minWidth: 24 }}>
                      {utilsDocs.renderDocIcon(item.documnetName, 24)}
                    </span>
                    <Tooltip message={item.documnetName}>
                      <Typography
                        classes={classNames("mg ml-4", {
                          "hover-text-underline":
                            !isAdmin &&
                            !hubWorkgroupList.includes(item.workgroupId),
                        })}
                        ellipsis
                        onClick={(e: any) => {
                          if (isAdmin) return;
                          e.stopPropagation();
                          item.syncId &&
                            !hubWorkgroupList.includes(item.workgroupId) &&
                            handleDocPreview({
                              permission: "view",
                              workgroupId: item.workgroupId,
                              syncId: item.syncId,
                              toast,
                              t,
                              revisionNo: item?.revisionNo,
                            });
                        }}
                      >
                        {item.documnetName}
                      </Typography>
                    </Tooltip>
                  </Box>
                ),
              },
              {
                columnText: (
                  <Tooltip
                    message={
                      item.workgroupId === "NONEWORKGROUP"
                        ? "My Box"
                        : item.workgroupName
                    }
                    width={"100%"}
                  >
                    <Typography width={"100%"} ellipsis>
                      {item.workgroupId === "NONEWORKGROUP"
                        ? "My Box"
                        : item.workgroupName}
                    </Typography>
                  </Tooltip>
                ),
              },
              {
                columnText: item.revisionNo,
              },
              {
                columnText: (
                  <Tooltip
                    message={utilsCommon.getNameWithId(
                      item.userName,
                      item.userId,
                      3
                    )}
                  >
                    <Typography ellipsis>
                      {utilsCommon.getNameWithId(item.userName, item.userId, 3)}
                    </Typography>
                  </Tooltip>
                ),
              },
              {
                columnText: item.policyLogs ? (
                  <Tooltip
                    toolTipMaxWidth={"max-content"}
                    message={
                      item.saveType === 10 &&
                      item.policyLogs[0].permissionPolicy.policy
                        ?.fileShareLinkPolicy ? (
                        <FilesharePolicyTooltip
                          linkPolicy={
                            item.policyLogs[0].permissionPolicy.policy
                              ?.fileShareLinkPolicy
                          }
                          logDate={item.logDate}
                        />
                      ) : (
                        <WorkgroupPolicyTooltip logItem={item.policyLogs} />
                      )
                    }
                  >
                    <Typography type="body2_rg" ellipsis>
                      {t(`log.filterDocument.${item.saveType}`)}
                    </Typography>
                  </Tooltip>
                ) : item.historyType === 1 && item.desc ? (
                  <Tooltip
                    message={
                      (item.saveType === 8
                        ? t("admin.filter.fileManager") + ":"
                        : item.saveType === 3
                        ? t("log.detailLog.pastFileName")
                        : "") + item.desc
                    }
                  >
                    <Typography type="body2_rg" ellipsis>
                      {t(`log.filterDocument.${item.saveType}`)}
                    </Typography>
                  </Tooltip>
                ) : (
                  <Typography type="body2_rg" ellipsis>
                    {t(`log.filterDocument.${item.saveType}`)}
                  </Typography>
                ),
              },
              {
                columnText: item.ip,
              },
              {
                columnText: getAgentTypeStr(item.agentType),
              },
            ],
            targetId: settingButtonInfo.logId,
            clickRAction:
              isAdmin || hubWorkgroupList.includes(item.workgroupId)
                ? undefined
                : (e: any) => {
                    e.stopPropagation();
                    setSettingInfo({
                      x: dir === "ltr" ? e.pageX - 245 : e.pageX,
                      y: e.pageY - 61,
                      logId: item.logId,
                      log: item,
                    });
                  },
            hover: !isAdmin && !hubWorkgroupList.includes(item.workgroupId) && (
              <Button
                btnStyle={"table-icon"}
                size="sm"
                classes="mg ml-auto"
                onClick={(e) => {
                  e.stopPropagation();
                  setSettingInfo({
                    x: e.pageX - 240,
                    y: e.pageY - 61,
                    logId: item.logId,
                    log: item,
                  });
                }}
                leadingIcon={<Icons variant="line" label="moreVertical" />}
              />
            ),
            id: item.logId,

            clickAction:
              // isAdmin || hubWorkgroupList.includes(item.workgroupId)
              isAdmin
                ? undefined
                : () => {
                    changeAsideId(item.logId);
                    setShowDocAside(true);
                  },
          };
          dataTransformed.push(dataItemTransformed);
        }
      });
    return dataTransformed;
  }
  const returnAsideItem = (log?: LogInfo) => {
    return log
      ? {
          syncId: log.syncId,
          revisionNo: log.revisionNo,
          workgroupId: log.workgroupId,
          contentName: log.documnetName,
        }
      : undefined;
  };
  return (
    <Box classes="adminDocLog" direction="column">
      <Box classes="adminDocLog-title" justifyContent="space-between">
        <Typography type="h1">
          {!window.location.href.includes("/admin") &&
          window.location.href.endsWith("/log")
            ? t("log.myPolicyTitle")
            : t("admin.menu.log")}
        </Typography>
        <Button
          type="secondary"
          btnStyle="fill"
          label={t("log.exportCSV")}
          leadingIcon={<Icons variant="line" label="export" />}
          onClick={() => {
            const excel_filter = {
              posPage: 1,
              pageRange: 1,
              rowRange: pageCount,
              filterDocument:
                filterData.saveTypes && filterData.saveTypes.length > 0
                  ? filterData.saveTypes
                  : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
              timezoneOffset: new Date().getTimezoneOffset(),
              startDate:
                filterData?.date?.start ?? utilsCommon.getStartPeriod("1month"),
              endDate: filterData?.date?.end ?? dayjs().endOf("day").valueOf(),
              order: filterData.order + "",
              userId: filterData.userId + "",
              documentName: filterData.fileName + "",
              workgroupId: filterData.workgroupId ?? "",
              workgroupIdList: filterData.workgroupIds ?? [],
            };
            if (isAdmin) getDocLogExcel(excel_filter);
            else getDocUserLogExcel(excel_filter);
          }}
          state={docLogs.length > 0 ? "default" : "disable"}
        />
      </Box>
      <Box classes="adminDocLog-title" justifyContent="space-between">
        <AdminFilter
          initFilterData={initFilterData}
          initNum={initNum}
          filterData={filterData}
          setFilterData={setFilterData}
          workgroupList={workgroupList}
        />
      </Box>
      {settingButtonInfo.logId && (
        <LogSettingButton
          settingButtonInfo={settingButtonInfo}
          setSettingInfo={setSettingInfo}
          page="fileLog"
        />
      )}
      <Box ref={tableRef} classes="mg mt-12" height={"calc(100% - 145px)"}>
        <Box direction="column" width={"100%"}>
          <Table
            width={"100%"}
            height={(docLogs.length === 0 ? 0 : height) + "px"}
            header={[
              {
                headerText: "",
                width: "16px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.log.date"),
                width: "164px",
                order:
                  filterData?.order === "date"
                    ? "upOn"
                    : filterData?.order === "datedesc"
                    ? "downOn"
                    : "downOff",
                orderAction: () => {
                  if (docLogs.length > 0) {
                    setFilterData({
                      ...filterData,
                      order:
                        filterData.order === "datedesc" ? "date" : "datedesc",
                    });
                    changeAsideId("");
                  }
                },
              },
              {
                headerText: t("admin.log.fileName"),
                width: "calc((100% - 745px) * 0.68)",
                order:
                  filterData?.order === "file"
                    ? "upOn"
                    : filterData?.order === "filedesc"
                    ? "downOn"
                    : "downOff",
                orderAction: () => {
                  if (docLogs.length > 0) {
                    setFilterData({
                      ...filterData,
                      order:
                        filterData.order === "filedesc" ? "file" : "filedesc",
                    });
                    changeAsideId("");
                  }
                },
              },
              {
                headerText: t("common.location"),
                width: "130px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.log.currentVersion"),
                width: "65px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.log.user"),
                width: "calc((100% - 745px) * 0.32)",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.filter.type"),
                width: "140px",
                order: "",
                orderAction: () => {},
              },

              {
                headerText: t("admin.log.ip"),
                width: "130px",
                order: "",
                orderAction: () => {},
              },
              {
                headerText: t("admin.log.platform"),
                width: "100px",
                order: "",
                orderAction: () => {},
              },
            ]}
            asideId={asideId}
            data={transformFileList(docLogs)}
            totalCount={docLogs.length}
            endReached={() => {
              getDocLogList(nextPage);
            }}
            targetId={settingButtonInfo.logId}
            sideMenu={isAdmin ? undefined : showDocAside ? "off" : "on"}
            sideMenuAction={() => setShowDocAside(!showDocAside)}
            columnHeight="md"
          />
          {docLogs.length === 0 &&
            (JSON.stringify(initFilterData) === JSON.stringify(filterData) ? (
              <EmptyInitFilterPage pageType={"myFile_log"} />
            ) : (
              <EmptyFilterPage
                handleInitFilter={() => {
                  setFilterData(initFilterData);
                  changeInitNum(initNum + 1);
                }}
              />
            ))}
        </Box>
        {showDocAside && (
          <DocAside
            asideInfo={returnAsideItem(
              docLogs.filter((item) => item.logId === asideId)[0]
            )}
          />
        )}
      </Box>
    </Box>
  );
};

export default DocLog;
